import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/api";
import type {
  PublicPlacementData,
  PublicPlacementsOutputDto,
} from "@/types/codegen";
import { BannersPlacementSlug } from "@/types/enums";

const allBannersPlacementSlugs = Object.values(BannersPlacementSlug);

type PlacementsBySlug = Record<
  BannersPlacementSlug,
  PublicPlacementData | null
>;

export const bannersPlacementsQueryOptions = () =>
  queryOptions({
    queryKey: ["placements", "all"] as const,
    queryFn: ({ signal }) =>
      api
        .get<PublicPlacementsOutputDto>("/placements", {
          signal,
          params: { slugs: allBannersPlacementSlugs },
        })
        .then((response) => {
          const placementsBySlug = Object.fromEntries(
            allBannersPlacementSlugs.map((slug) => [slug, null]),
          ) as PlacementsBySlug;

          response.data.data.placements.forEach((placement) => {
            placementsBySlug[placement.slug as BannersPlacementSlug] =
              placement;
          });

          return placementsBySlug;
        }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

export const useBannersPlacementQuery = (slug: BannersPlacementSlug) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["placements", slug],
    queryFn: async ({ signal, client }) => {
      const batchState = client.getQueryState(
        bannersPlacementsQueryOptions().queryKey,
      );

      if (batchState?.fetchStatus === "fetching") {
        // Join the in-flight batch request via deduplication
        const placementsBySlug = await client.fetchQuery(
          bannersPlacementsQueryOptions(),
        );
        return placementsBySlug[slug];
      }

      return api
        .get<PublicPlacementsOutputDto>("/placements", {
          signal,
          params: { slugs: [slug] },
        })
        .then(
          (response) =>
            response.data.data.placements.find(
              (placement) => placement.slug === slug,
            ) ?? null,
        );
    },
    initialData: () =>
      queryClient.getQueryData<PlacementsBySlug>(
        bannersPlacementsQueryOptions().queryKey,
      )?.[slug],
    initialDataUpdatedAt: () =>
      queryClient.getQueryState(bannersPlacementsQueryOptions().queryKey)
        ?.dataUpdatedAt,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
